import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { ConversationFilters, ConversationSummary, useConversations } from '../../queries/supportChat';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/utils';
import { ConversationThread } from './ConversationThread';

const SELECT =
  'h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const TIME = new Intl.DateTimeFormat('en-AU', { timeStyle: 'short' });
const DAY = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short' });

function when(iso: string): string {
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString() ? TIME.format(d) : DAY.format(d);
}

function ConversationRow({ c, active, onSelect }: { c: ConversationSummary; active: boolean; onSelect: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? 'true' : undefined}
        className={cn(
          'flex w-full flex-col gap-0.5 border-b border-border px-4 py-3 text-left transition-colors hover:bg-muted/60',
          active && 'bg-muted',
        )}
      >
        <span className="flex items-center justify-between gap-2">
          <span className={cn('truncate text-sm', c.unread ? 'font-semibold' : 'font-medium')}>
            {c.memberName ?? c.memberEmail ?? `User ${c.userId}`}
          </span>
          <span className="shrink-0 text-[11px] text-muted-foreground">{when(c.lastMessageAt)}</span>
        </span>
        <span className="flex items-center gap-2">
          {c.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />}
          <span className={cn('line-clamp-1 text-xs', c.unread ? 'text-foreground' : 'text-muted-foreground')}>
            {c.lastSender === 'staff' && 'You: '}
            {c.lastMessage ?? 'No messages yet'}
          </span>
          {c.lastSender === 'staff' && c.memberSeenReply != null && (
            <span className={cn('ml-auto shrink-0 text-[11px]', c.memberSeenReply ? 'text-success' : 'text-muted-foreground')}>
              {c.memberSeenReply ? 'Seen' : 'Not seen'}
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

export function MessagesPage() {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const selectedId = conversationId ? Number(conversationId) : null;

  const [status, setStatus] = useState<ConversationFilters['status']>('open');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [status, unreadOnly, q]);
  // Search on pause, not on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useConversations(page, { status, unread: unreadOnly, q: q || undefined });
  const conversations = data?.conversations ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Messages"
        description="Questions and feedback members send from the chat on the site. Unread conversations come first."
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <select value={status} onChange={(e) => setStatus(e.target.value as ConversationFilters['status'])} aria-label="Filter by status" className={SELECT}>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="all">All</option>
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
          Unread only
        </label>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email"
          aria-label="Search conversations"
          className={cn(SELECT, 'sm:ml-auto sm:w-64')}
        />
      </div>

      <Card className="grid overflow-hidden p-0 lg:grid-cols-[minmax(260px,340px)_1fr]">
        <section aria-label="Conversations" className={cn('border-border lg:border-r', selectedId && 'hidden lg:block')}>
          {isLoading ? (
            <Spinner />
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground">
              <MessageSquare className="h-6 w-6" aria-hidden="true" />
              No conversations here yet.
            </div>
          ) : (
            <ul>
              {conversations.map((c) => (
                <ConversationRow key={c.id} c={c} active={c.id === selectedId} onSelect={() => navigate(`/messages/${c.id}`)} />
              ))}
            </ul>
          )}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between p-3 text-xs text-muted-foreground">
              <span>Page {pagination.page} of {pagination.pages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pagination.page <= 1}>Previous</Button>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))} disabled={pagination.page >= pagination.pages}>Next</Button>
              </div>
            </div>
          )}
        </section>

        <section aria-label="Conversation" className={cn(!selectedId && 'hidden lg:block')}>
          {selectedId ? (
            <>
              <Button variant="ghost" size="sm" className="m-2 lg:hidden" onClick={() => navigate('/messages')}>
                ← All conversations
              </Button>
              <ConversationThread key={selectedId} conversationId={selectedId} />
            </>
          ) : (
            <div className="flex h-full min-h-[420px] items-center justify-center p-10 text-sm text-muted-foreground">
              Pick a conversation to read it.
            </div>
          )}
        </section>
      </Card>
    </div>
  );
}
